import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

/**
 * Cinematic brand-film embed for the homepage.
 *
 * Native <video> with two H.264/AAC sources and `<source media>` queries:
 *   • capexara-brand-film-mobile.mp4 — 1280×720, ~13 MB (≤ 900 px viewport)
 *   • capexara-brand-film.mp4        — 1920×1080, ~40 MB (everything else)
 *
 * Both files are remuxed with `+faststart` so the moov atom is at the front
 * and playback can begin while the rest of the file is still streaming.
 */
export default function BrandFilm() {
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const [played, setPlayed] = useState(false);

  // Pause when the section scrolls out of view — saves bandwidth + battery.
  useEffect(() => {
    const v = videoRef.current;
    const s = sectionRef.current;
    if (!v || !s || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && !v.paused) v.pause();
        });
      },
      { threshold: 0.15 }
    );
    io.observe(s);
    return () => io.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="brand-film reveal" data-testid="home-brand-film">
      <div className="container">
        <div className="brand-film-head">
          <div className="section-label">
            <span className="dot" />The Capexara film
          </div>
          <h2 className="brand-film-h2 display">
            Capital projects, <em>cognitively rebuilt.</em>
          </h2>
          <p className="brand-film-sub">
            Two minutes on what we believe, who we serve, and how a single cognitive substrate
            unifies eight AI platforms into one decision engine for capital projects.
          </p>
        </div>

        <div className="brand-film-frame" data-played={played ? "true" : "false"}>
          <video
            ref={videoRef}
            controls
            playsInline
            preload="metadata"
            poster="/media/capexara-brand-film-poster.jpg"
            onPlay={() => setPlayed(true)}
            data-testid="home-brand-film-video"
          >
            <source
              src="/media/capexara-brand-film-mobile.mp4"
              type="video/mp4"
              media="(max-width: 900px)"
            />
            <source src="/media/capexara-brand-film.mp4" type="video/mp4" />
            Your browser does not support embedded video. You can
            {" "}
            <a href="/media/capexara-brand-film.mp4">download the film</a> instead.
          </video>
          <div className="brand-film-frame-glow" aria-hidden="true" />
        </div>

        <div className="brand-film-meta">
          <span className="brand-film-meta-item">
            <span className="bf-dot" />1080p · 2 min 25 s
          </span>
          <span className="brand-film-meta-divider" aria-hidden="true">/</span>
          <span className="brand-film-meta-item">Sound on, but readable without it</span>
          <Link to="/platforms" className="brand-film-link" data-testid="brand-film-explore">
            Explore the eight platforms <span className="arrow">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}
